import { motion } from "framer-motion";
import { X } from "lucide-react";
import depressedChurchBg from "@/assets/depressed-church.jpg";

const abusivePatterns = [
  "Silence survivors",
  "Spiritualize harm",
  'Call abuse "misunderstanding"',
  'Call accountability "rebellion"',
  'Call discernment "division"',
];

export function ElephantInTheRoom() {
  return (
    <section
      className="relative py-32 md:py-40 overflow-hidden"
      style={{
        backgroundImage: `url(${depressedChurchBg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      {/* Cinematic overlay */}
      <div className="absolute inset-0 bg-black/80 backdrop-blur-[2px]" />

      <div className="container relative z-10 px-6">
        {/* Eyebrow */}
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center text-xs font-semibold uppercase tracking-[0.35em] text-accent mb-6"
          style={{ fontFamily: "'DM Sans', sans-serif" }}
        >
          Let's Address the Elephant in the Room
        </motion.p>

        {/* Editorial headline — massive, commanding */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-20 max-w-5xl mx-auto"
        >
          <h2
            className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black leading-[0.95] tracking-[-0.03em] text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Churches that gaslight abuse
            <br className="hidden sm:block" />
            {" "}are not{" "}
            <span className="italic text-accent">"misunderstood."</span>
          </h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-6 text-3xl sm:text-4xl md:text-5xl font-black text-white"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            They are{" "}
            <span className="text-destructive italic">unsafe.</span>
          </motion.p>
        </motion.div>

        {/* Pattern Recognition card — full-width on desktop */}
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 25 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <div className="bg-card/95 backdrop-blur-md border border-border/30 rounded-2xl shadow-[0_30px_80px_-20px_rgba(0,0,0,0.5)] overflow-hidden">
              {/* Clip notch */}
              <div className="flex items-center justify-center py-4 bg-destructive/10 border-b border-border/20">
                <div className="w-20 h-2 rounded-full bg-destructive/30" />
              </div>

              {/* Header */}
              <div className="px-8 md:px-12 pt-8 pb-5 border-b border-border/15">
                <p
                  className="text-xs md:text-sm font-bold uppercase tracking-[0.25em] text-destructive/70 mb-2"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  Pattern Recognition
                </p>
                <h3
                  className="text-xl md:text-2xl lg:text-3xl font-bold text-foreground"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Many Christian Spaces Have Learned How To
                </h3>
              </div>

              {/* Checklist */}
              <div className="px-8 md:px-12 py-8 space-y-5 md:space-y-6">
                {abusivePatterns.map((pattern, i) => (
                  <motion.div
                    key={pattern}
                    initial={{ opacity: 0, x: -15 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: 0.3 + i * 0.08 }}
                    className="flex items-start gap-4"
                  >
                    <div className="w-7 h-7 rounded border-2 border-destructive/50 flex items-center justify-center shrink-0 mt-0.5">
                      <X className="w-4 h-4 text-destructive" />
                    </div>
                    <span
                      className="text-base md:text-lg lg:text-xl text-foreground/80 leading-relaxed"
                      style={{ fontFamily: "'DM Sans', sans-serif" }}
                    >
                      {pattern}
                    </span>
                  </motion.div>
                ))}
              </div>

              <div className="px-8 md:px-12 py-4 bg-muted/40 border-t border-border/15">
                <span
                  className="text-xs md:text-sm text-destructive/60 font-bold uppercase tracking-[0.2em]"
                  style={{ fontFamily: "'DM Sans', sans-serif" }}
                >
                  Harmful Patterns
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
